import React from "react";

// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
// core components
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardBody.jsx";
import Table from "common/components/Table/Table.jsx";
import Icon from "@material-ui/core/Icon";
import Button from "common/components/CustomButtons/Button.jsx";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import loadContent from "core-module/hoc/loadContent";
import config from "config";
import store from "store";
import { cardTitle } from "assets/jss/material-dashboard-pro-react.jsx";
import { isNativeIosPlatform } from "../../deviceInfo";

import buttonsStyle from "assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx";
import extendedTablesStyle from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.jsx";

const styles = {
  ...buttonsStyle,
  ...extendedTablesStyle,
  center: {
    ...extendedTablesStyle.center,
  },
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  maxWidth500: {
    maxWidth: "1400px",
    display: "flex",
    justifyContent: "center",
  },
  fixedWidth: {
    width: "90px",
  },
  documentIcon: {
    fontSize: "30px", // a little bigger
    color: "#6c6c6cff", // dark grey
  },
  documentsTable: {
    "& img": {
      display: "block",
      maxWidth: "120px",
      width: "100%",
      height: "auto",
    },
    "@media (max-width: 600px)": {
      overflowX: "hidden",
      "& table": {
        tableLayout: "fixed",
      },
      "& th, & td": {
        padding: "8px 3px !important",
        fontSize: "0.75rem !important",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      },
      "& th:nth-child(1), & td:nth-child(1)": {
        width: "22%",
      },
      "& th:nth-child(2), & td:nth-child(2)": {
        width: "25%",
      },
      "& th:nth-child(3), & td:nth-child(3)": {
        width: "22%",
      },
      "& th:nth-child(4), & td:nth-child(4)": {
        width: "31%",
      },
      "& img": {
        maxWidth: "64px",
      },
      "& $documentDownloadButton": {
        minWidth: 0,
        width: "100%",
        margin: 0,
        padding: "8px 3px",
        fontSize: "10px",
      },
    },
  },
  documentDownloadButton: {},
  documentLink: {
    appearance: "none",
    background: "none",
    border: 0,
    color: "inherit",
    cursor: "pointer",
    font: "inherit",
    padding: 0,
  },
  documentTextLink: {
    textDecoration: "underline",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  viewerContent: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    padding: 0,
  },
  viewerMessage: {
    alignSelf: "center",
    padding: "24px",
  },
  viewerFrame: {
    border: 0,
    height: "100%",
    width: "100%",
  },
};

/**
 * Documents table with an in-app viewer.
 */
export class DocumentsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      viewerError: "",
      viewerLoading: false,
      viewerOpen: false,
      viewerTitle: "",
      viewerURL: null,
    };

    const storeState = store.getState();
    this.jrr = storeState.services.api.JRR;
    this.objectURLs = new Set();
    this.viewRequestId = 0;
  }

  componentDidMount() {
    const storeState = store.getState();
    this.mounted = true;
    this.props.getList(
      async (oGet, id) => {
        const docRes = await fetch(
          config.API.ADDRESS +
            `?target=user.${this.props.ids.user}/${this.props.documentEndpoint}.${id}&tn=t`,
          { headers: { "x-omicron": storeState.services.api.JRR } }
        );

        if (this.mounted === false) {
          return null;
        }

        const docContent = await docRes.blob();
        const docURL = URL.createObjectURL(docContent);
        this.objectURLs.add(docURL);

        if (this.mounted === false) {
          URL.revokeObjectURL(docURL);
          this.objectURLs.delete(docURL);
          return null;
        }
        this.setState({ ["docURL" + id]: docURL });

        return null;
      },
      () => [],
      "DESC"
    );
  }

  componentWillUnmount() {
    this.mounted = false;
    this.viewRequestId += 1;
    this.objectURLs.forEach((url) => URL.revokeObjectURL(url));
    this.objectURLs.clear();
  }

  viewDocument = async (id, description) => {
    const requestId = this.viewRequestId + 1;
    this.viewRequestId = requestId;
    if (this.state.viewerURL) {
      URL.revokeObjectURL(this.state.viewerURL);
      this.objectURLs.delete(this.state.viewerURL);
    }
    this.setState({
      viewerError: "",
      viewerLoading: true,
      viewerOpen: true,
      viewerTitle: description || "Document",
      viewerURL: null,
    });

    try {
      const docRes = await fetch(
        config.API.ADDRESS +
          `?target=user.${this.props.ids.user}/${this.props.documentEndpoint}.${id}`,
        {
          headers: {
            "x-omicron": store.getState().services.api.JRR,
          },
        }
      );

      if (!docRes.ok) {
        throw new Error(`Unable to open document (${docRes.status}).`);
      }

      const docContent = await docRes.blob();
      const docURL = URL.createObjectURL(docContent);

      if (this.mounted === false || requestId !== this.viewRequestId) {
        URL.revokeObjectURL(docURL);
        return;
      }

      this.objectURLs.add(docURL);
      this.setState({
        viewerLoading: false,
        viewerURL: docURL,
      });
    } catch (error) {
      if (this.mounted && requestId === this.viewRequestId) {
        this.setState({
          viewerError: error.message || "Unable to open this document.",
          viewerLoading: false,
        });
      }
    }
  };

  closeViewer = () => {
    this.viewRequestId += 1;
    if (this.state.viewerURL) {
      URL.revokeObjectURL(this.state.viewerURL);
      this.objectURLs.delete(this.state.viewerURL);
    }

    this.setState({
      viewerError: "",
      viewerLoading: false,
      viewerOpen: false,
      viewerTitle: "",
      viewerURL: null,
    });
  };

  render() {
    const { classes } = this.props;
    const useNativeIosLayout = isNativeIosPlatform();
    const openButton = (id, description) => {
      const downloadURL =
        config.API.ADDRESS +
        `?target=user.${this.props.ids.user}/${this.props.documentEndpoint}.${id}&x-omicron=${this.jrr}`;

      return (
        <div className={classes.actions}>
          <Button
            color="primaryRed"
            className={
              useNativeIosLayout ? classes.documentDownloadButton : undefined
            }
            size={useNativeIosLayout ? "sm" : undefined}
            onClick={() => this.viewDocument(id, description)}
          >
            View
          </Button>
          <Button
            className={
              useNativeIosLayout ? classes.documentDownloadButton : undefined
            }
            size={useNativeIosLayout ? "sm" : undefined}
            href={downloadURL}
          >
            Download
          </Button>
        </div>
      );
    };

    return (
      <>
        <GridContainer justify="center">
          <GridItem xs={12} className={classes.maxWidth500}>
            <Card>
              <CardHeader color="primary" icon>
                <CardIcon color="primary">
                  <Icon>all_inbox</Icon>
                </CardIcon>
                <h4 className={classes.cardIconTitle}>{this.props.title}</h4>
              </CardHeader>
              <CardBody>
                <Table
                  tableHeaderColor="primary"
                  tableHead={["Preview", "Description", "Date", ""]}
                  tableData={this.props.getList(
                    (oGet, id) => {
                      const description = oGet("description");
                      const viewLabel = `View ${description || "document"}`;

                      return [
                        <button
                          key={id}
                          type="button"
                          className={classes.documentLink}
                          aria-label={viewLabel}
                          onClick={() => this.viewDocument(id, description)}
                        >
                          {`docURL${id}` in this.state ? (
                            <img alt="" src={this.state[`docURL${id}`]} />
                          ) : (
                            <Icon className={classes.documentIcon}>
                              description
                            </Icon>
                          )}
                        </button>,
                        <button
                          type="button"
                          className={`${classes.documentLink} ${classes.documentTextLink}`}
                          onClick={() => this.viewDocument(id, description)}
                        >
                          {description}
                        </button>,
                        <button
                          type="button"
                          className={`${classes.documentLink} ${classes.documentTextLink}`}
                          onClick={() => this.viewDocument(id, description)}
                        >
                          {oGet("ts")}
                        </button>,
                        openButton(id, description),
                      ];
                    },
                    () => [
                      ["", <h3>No documents found on this account.</h3>, ""],
                    ],
                    "DESC"
                  )}
                  customCellClasses={[
                    classes.fixedWidth,
                    classes.center,
                    classes.center,
                    classes.right,
                  ]}
                  customClassesForCells={[0, 1, 2, 3]}
                  customHeadCellClasses={[
                    classes.fixedWidth,
                    classes.center,
                    classes.center,
                    classes.right,
                  ]}
                  customHeadClassesForCells={[0, 1, 2, 3]}
                  tableCustomClasses={
                    useNativeIosLayout ? classes.documentsTable : undefined
                  }
                />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
        <Dialog
          fullScreen
          open={this.state.viewerOpen}
          onClose={this.closeViewer}
        >
          <DialogTitle>{this.state.viewerTitle}</DialogTitle>
          <DialogContent className={classes.viewerContent}>
            {this.state.viewerLoading && (
              <div className={classes.viewerMessage}>Opening document…</div>
            )}
            {this.state.viewerError && (
              <div className={classes.viewerMessage} role="alert">
                {this.state.viewerError}
              </div>
            )}
            {this.state.viewerURL && (
              <iframe
                className={classes.viewerFrame}
                src={this.state.viewerURL}
                title={this.state.viewerTitle}
              />
            )}
          </DialogContent>
          <DialogActions>
            {this.state.viewerURL && (
              <Button color="primary" href={this.state.viewerURL} download>
                Download a copy
              </Button>
            )}
            <Button onClick={this.closeViewer}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

/**
 * Wrapped components for the two document lists.
 */
const Documents = withStyles(styles)(
  loadContent("user.session/document", DocumentsTable)
);

const ContractDocuments = withStyles(styles)(
  loadContent("user.session/contractdocument", DocumentsTable)
);

/**
 * Page that shows both sections, one after the other
 */
const DocumentsPage = () => (
  <>
    <ContractDocuments
      documentEndpoint="contractdocumentget"
      title="Contract Documents"
    />
    <Documents documentEndpoint="documentget" title="Billing Documents" />
  </>
);

// Default export is still Documents to not break existing imports
export default DocumentsPage;
